import React, { useState, useEffect } from 'react';
import { api } from '../../App';
import toast from 'react-hot-toast';

function GcalCard() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/api/hiring/gcal/status').then(r => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.post('/api/hiring/gcal/connect');
      if (r.data.mode === 'oauth') { window.location.href = r.data.authorization_url; return; }
      toast.success(r.data.message, { duration: 6000 });
      load();
    } catch { toast.error('Could not connect calendar'); }
    setBusy(false);
  };
  const disconnect = async () => {
    try { await api.post('/api/hiring/gcal/disconnect'); toast.success('Calendar disconnected'); load(); }
    catch { toast.error('Failed'); }
  };

  if (!st) return null;
  return (
    <div className="bg-surface rounded-2xl border border-border p-5" data-testid="gcal-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-text-primary">
            <i className="fa-brands fa-google text-primary mr-2" />Google Calendar
            <span className="ml-2 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700">Beta</span>
          </p>
          {st.connected ? (
            <p className="text-xs mt-1" data-testid="gcal-status-text">
              <span className={`font-bold ${st.simulated ? 'text-amber-600' : 'text-emerald-600'}`}>
                <i className="fa-solid fa-circle-check mr-1" />Connected{st.simulated ? ' (simulated)' : ''}
              </span>
              <span className="text-text-muted"> · {st.email} · {st.events_created} event(s) created</span>
              {st.simulated && <span className="block text-amber-600 mt-0.5">Bookings log simulated events — add Google credentials to create real calendar events.</span>}
            </p>
          ) : (
            <p className="text-xs text-text-muted mt-1" data-testid="gcal-status-text">Not connected — bookings won't appear on a calendar yet.{!st.configured && ' Connecting now runs in simulated beta mode.'}</p>
          )}
        </div>
        {st.connected
          ? <button onClick={disconnect} className="btn-secondary text-xs" data-testid="gcal-disconnect-btn"><i className="fa-solid fa-link-slash mr-1.5" />Disconnect</button>
          : <button onClick={connect} disabled={busy} className="btn-primary text-xs" data-testid="gcal-connect-btn">{busy ? <i className="fa-solid fa-circle-notch fa-spin mr-1.5" /> : <i className="fa-brands fa-google mr-1.5" />}Connect Google Calendar</button>}
      </div>
    </div>
  );
}

export default function Interviews() {
  const [slots, setSlots] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({ start: '', end: '', label: 'Interview', job_id: '' });

  const load = async () => {
    try {
      const [s, j, e] = await Promise.all([
        api.get('/api/hiring/slots'), api.get('/api/hiring/jobs'),
        api.get('/api/hiring/gcal/events').catch(() => ({ data: { events: [] } })),
      ]);
      setSlots(s.data.slots || []);
      setJobs(j.data.jobs || []);
      setEvents(e.data.events || []);
    } catch { toast.error('Failed to load interviews'); }
  };
  useEffect(() => { load(); }, []);

  const addSlot = async (e) => {
    e.preventDefault();
    if (!form.start || !form.end) return toast.error('Pick a start and end time');
    try {
      await api.post('/api/hiring/slots', {
        slots: [{
          start: new Date(form.start).toISOString(),
          end: new Date(form.end).toISOString(),
          label: form.label, job_id: form.job_id || null,
        }],
      });
      setForm({ start: '', end: '', label: 'Interview', job_id: '' });
      load(); toast.success('Availability added');
    } catch { toast.error('Could not add slot'); }
  };
  const remove = async (id) => {
    try { await api.delete(`/api/hiring/slots/${id}`); load(); } catch { toast.error('Delete failed'); }
  };
  const remind = async (id) => {
    try { const r = await api.post(`/api/hiring/slots/${id}/remind`); toast.success(r.data.message); }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not send reminder'); }
  };

  const open = slots.filter(s => s.status === 'open');
  const booked = slots.filter(s => s.status === 'booked');
  const fmt = (iso) => new Date(iso).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

  return (
    <div className="space-y-5" data-testid="interviews-panel">
      <GcalCard />

      <div className="bg-surface rounded-2xl border border-border p-6">
        <p className="text-sm font-bold text-text-primary mb-1"><i className="fa-solid fa-calendar-plus text-primary mr-2" />Set your interview availability</p>
        <p className="text-xs text-text-muted mb-4">Applicants pick an open time on the public careers page. Booked slots move the candidate to the Interview stage automatically — and land on your connected calendar.</p>
        <form onSubmit={addSlot} className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          <label className="text-[10px] font-semibold uppercase text-text-muted">Start
            <input type="datetime-local" value={form.start} onChange={e => setForm({ ...form, start: e.target.value })} className="input-field text-sm mt-1" data-testid="slot-start-input" />
          </label>
          <label className="text-[10px] font-semibold uppercase text-text-muted">End
            <input type="datetime-local" value={form.end} onChange={e => setForm({ ...form, end: e.target.value })} className="input-field text-sm mt-1" data-testid="slot-end-input" />
          </label>
          <label className="text-[10px] font-semibold uppercase text-text-muted">Label
            <input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} className="input-field text-sm mt-1" />
          </label>
          <label className="text-[10px] font-semibold uppercase text-text-muted">Job (optional)
            <select value={form.job_id} onChange={e => setForm({ ...form, job_id: e.target.value })} className="input-field text-sm mt-1">
              <option value="">Any job</option>
              {jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
            </select>
          </label>
          <button type="submit" className="btn-primary text-sm" data-testid="add-slot-btn"><i className="fa-solid fa-plus mr-1.5" />Add slot</button>
        </form>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-surface rounded-2xl border border-border p-5">
          <p className="text-xs font-bold uppercase text-text-secondary mb-3">Open availability ({open.length})</p>
          <div className="space-y-2">
            {open.length === 0 && <p className="text-xs text-text-muted opacity-70">No open slots — add some above.</p>}
            {open.map(s => (
              <div key={s.id} className="flex items-center justify-between bg-surface-alt rounded-xl px-3 py-2" data-testid="open-slot-row">
                <span className="text-xs text-text-secondary"><i className="fa-regular fa-clock mr-1.5 text-primary" />{fmt(s.start)} · {s.label}</span>
                <button onClick={() => remove(s.id)} className="text-rose-500 text-xs" data-testid="delete-slot-btn"><i className="fa-solid fa-trash-can" /></button>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-surface rounded-2xl border border-border p-5">
          <p className="text-xs font-bold uppercase text-text-secondary mb-3">Booked interviews ({booked.length})</p>
          <div className="space-y-2">
            {booked.length === 0 && <p className="text-xs text-text-muted opacity-70">No interviews booked yet.</p>}
            {booked.map(s => (
              <div key={s.id} className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2" data-testid="booked-slot-row">
                <span className="text-xs text-emerald-800"><i className="fa-solid fa-user-check mr-1.5" /><b>{s.candidate_name}</b> · {fmt(s.start)}</span>
                <button onClick={() => remind(s.id)} className="text-[10px] font-bold uppercase text-emerald-700 hover:underline" data-testid="send-reminder-btn"><i className="fa-solid fa-bell mr-1" />Remind</button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {events.length > 0 && (
        <div className="bg-surface rounded-2xl border border-border p-5" data-testid="gcal-events-panel">
          <p className="text-xs font-bold uppercase text-text-secondary mb-3"><i className="fa-brands fa-google mr-1.5 text-primary" />Calendar events ({events.length})</p>
          <div className="space-y-2">
            {events.slice(0, 8).map(e => (
              <div key={e.id} className="flex items-center justify-between bg-surface-alt rounded-xl px-3 py-2" data-testid="gcal-event-row">
                <span className="text-xs text-text-secondary truncate"><i className="fa-regular fa-calendar mr-1.5 text-primary" />{e.summary} · {fmt(e.start)}</span>
                <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full shrink-0 ${e.simulated ? 'bg-amber-100 text-amber-700' : e.status === 'created' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-600'}`}>{e.simulated ? 'Simulated' : e.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
